import { useEffect, useState } from "react";
import { Loader2, Play, Plus, Sparkles, Wand2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const STAGE_COLORS = { done: "text-emerald-400", error: "text-red-400", running: "text-[#C9A24B]" };

export const ReSeoAgentTab = () => {
  const [cfg, setCfg] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [topic, setTopic] = useState("");
  const [publish, setPublish] = useState(false);
  const [busy, setBusy] = useState("");
  const [newTopic, setNewTopic] = useState("");

  const loadCfg = () => api.get("/admin/realestate/seo-agent/config").then((r) => setCfg(r.data)).catch(() => setCfg({ queue: [], competitor_urls: [] }));
  const loadJobs = () => api.get("/admin/realestate/seo-agent/jobs").then((r) => setJobs(r.data || [])).catch(() => {});
  useEffect(() => { loadCfg(); loadJobs(); const t = setInterval(loadJobs, 8000); return () => clearInterval(t); }, []);

  const saveCfg = async (patch) => {
    try { const r = await api.put("/admin/realestate/seo-agent/config", { ...patch }); setCfg(r.data); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  const run = async () => {
    if (topic.trim().length < 3) return toast.error("Give the agent a topic");
    setBusy("run");
    try { await api.post("/admin/realestate/seo-agent/run", { topic: topic.trim(), publish }); toast.success("Writing started — it appears below shortly"); setTopic(""); loadJobs(); }
    catch (e) { toast.error(formatApiErrorDetail(e)); } finally { setBusy(""); }
  };

  const autopilotTopics = async () => {
    setBusy("topics");
    try { const r = await api.post("/admin/realestate/seo-agent/autopilot-topics"); setCfg((c) => ({ ...c, queue: r.data.queue })); toast.success(`${r.data.added} fresh topics added`); }
    catch (e) { toast.error(formatApiErrorDetail(e)); } finally { setBusy(""); }
  };

  const discover = async () => {
    setBusy("comp");
    try { const r = await api.post("/admin/realestate/seo-agent/discover-competitors"); setCfg((c) => ({ ...c, competitor_urls: r.data.competitor_urls })); toast.success("Competitors discovered"); }
    catch (e) { toast.error(formatApiErrorDetail(e)); } finally { setBusy(""); }
  };

  if (!cfg) return <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#C9A24B]" /></div>;

  return (
    <div data-testid="re-seo-agent-tab" className="space-y-6">
      <div>
        <h2 className="font-serif text-2xl font-semibold text-[#1A1615]">SEO AI Agent</h2>
        <p className="mt-1 text-xs text-[#91837C]">Writes Dubai property market insights &amp; buyer guides straight into your Insights — on the free AI cascade.</p>
      </div>

      <div className="glass rounded-2xl p-6">
        <label className="text-xs font-semibold uppercase tracking-[0.15em] text-[#B38B2D]">Write an article now</label>
        <div className="mt-3 flex flex-col gap-3 sm:flex-row">
          <input data-testid="re-seo-topic-input" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Golden Visa through Dubai property in 2026"
            className="flex-1 rounded-xl border border-[#E6DCD2] bg-white px-4 py-3 text-sm text-[#1A1615] outline-none focus:border-[#C9A24B]" />
          <button data-testid="re-seo-run" onClick={run} disabled={busy === "run"}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#B38B2D] px-5 py-3 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-60">
            {busy === "run" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Generate
          </button>
        </div>
        <label className="mt-3 flex items-center gap-2 text-xs text-[#6B5E57]">
          <input type="checkbox" data-testid="re-seo-publish" checked={publish} onChange={(e) => setPublish(e.target.checked)} className="h-4 w-4 accent-[#B38B2D]" />
          Publish immediately (otherwise saved as a draft in Insights to review)
        </label>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-[0.15em] text-[#B38B2D]">Topic queue</span>
            <button data-testid="re-seo-autopilot-topics" onClick={autopilotTopics} disabled={busy === "topics"} className="inline-flex items-center gap-1.5 rounded-full border border-[#C9A24B]/40 px-3 py-1.5 text-xs text-[#6B5E57] hover:bg-[#C9A24B]/10">
              {busy === "topics" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />} Suggest topics
            </button>
          </div>
          <div className="mt-3 flex gap-2">
            <input data-testid="re-seo-queue-input" value={newTopic} onChange={(e) => setNewTopic(e.target.value)} placeholder="Add a topic" className="flex-1 rounded-lg border border-[#E6DCD2] bg-white px-3 py-2 text-sm outline-none focus:border-[#C9A24B]" />
            <button onClick={() => { if (newTopic.trim()) { saveCfg({ queue: [...(cfg.queue || []), newTopic.trim()] }); setNewTopic(""); } }} className="rounded-lg border border-[#E6DCD2] px-3 py-2 text-[#6B5E57] hover:bg-[#F6F0E8]"><Plus className="h-4 w-4" /></button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {(cfg.queue || []).length === 0 && <p className="text-xs text-[#91837C]">No queued topics yet.</p>}
            {(cfg.queue || []).map((t, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 rounded-full bg-[#F6F0E8] px-3 py-1.5 text-xs text-[#6B5E57]">
                <button onClick={() => { setTopic(t); }} className="hover:text-[#B38B2D]">{t}</button>
                <button onClick={() => saveCfg({ queue: cfg.queue.filter((_, j) => j !== i) })} className="text-[#B5A79E] hover:text-red-400"><X className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
        </div>

        <div className="glass rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-[0.15em] text-[#B38B2D]">Competitors</span>
            <button data-testid="re-seo-discover" onClick={discover} disabled={busy === "comp"} className="inline-flex items-center gap-1.5 rounded-full border border-[#C9A24B]/40 px-3 py-1.5 text-xs text-[#6B5E57] hover:bg-[#C9A24B]/10">
              {busy === "comp" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Discover
            </button>
          </div>
          <div className="mt-3 space-y-1.5">
            {(cfg.competitor_urls || []).length === 0 && <p className="text-xs text-[#91837C]">No competitors tracked yet.</p>}
            {(cfg.competitor_urls || []).slice(0, 12).map((u, i) => (
              <a key={i} href={u} target="_blank" rel="noreferrer" className="block truncate text-xs text-[#6B5E57] hover:text-[#B38B2D]">{u}</a>
            ))}
          </div>
        </div>
      </div>

      <div className="glass rounded-2xl p-6">
        <span className="text-xs font-semibold uppercase tracking-[0.15em] text-[#B38B2D]">Recent runs</span>
        <div className="mt-3 space-y-2">
          {jobs.length === 0 && <p className="text-xs text-[#91837C]">No runs yet.</p>}
          {jobs.map((j) => (
            <div key={j.id} data-testid={`re-seo-job-${j.id}`} className="flex items-center justify-between rounded-lg border border-[#EFE7DD] px-4 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm text-[#1A1615]">{j.topic}</p>
                <p className="text-xs text-[#91837C]">{j.detail || j.stage}</p>
              </div>
              <span className={`text-xs font-semibold ${STAGE_COLORS[j.status] || "text-[#91837C]"}`}>{j.status}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
